import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const TradingHistorySummary = ({ onViewAll }) => {
  const { tokens } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchRecentTrades();
    }
  }, [tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchRecentTrades = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: '5', // Only get last 5 trades for summary
        offset: '0'
      });

      const data = await makeAuthenticatedRequest(`/trading/history?${params}`);
      setTrades(data.data.trades || []);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching recent trades:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, decimals = 6) => {
    const num = parseFloat(amount) / Math.pow(10, decimals);
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTokenSymbol = (tokenAddress) => {
    const KNOWN_TOKENS = {
      'So11111111111111111111111111111111111111112': 'SOL',
      'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
      'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 'USDT'
    };
    return KNOWN_TOKENS[tokenAddress] || `${tokenAddress.slice(0, 4)}...${tokenAddress.slice(-4)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const calculateStats = () => {
    const completedTrades = trades.filter(trade => trade.status === 'completed');
    const totalTrades = trades.length;
    const successRate = totalTrades > 0 ? (completedTrades.length / totalTrades * 100).toFixed(0) : 0;
    
    return {
      totalTrades,
      completedTrades: completedTrades.length,
      successRate
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
        <div style={{ 
          width: '20px', 
          height: '20px', 
          border: '2px solid #f3f3f3',
          borderTop: '2px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 10px'
        }}></div>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '12px', 
        background: '#f8d7da', 
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        color: '#721c24',
        fontSize: '14px'
      }}>
        Error loading trades: {error}
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '20px', 
        color: '#6c757d' 
      }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
        <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>No trades yet</p>
        <p style={{ margin: '0', fontSize: '14px' }}>Start trading to see your history here</p>
      </div>
    );
  }

  return (
    <div>
      {/* Quick Stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-around',
        marginBottom: '16px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '6px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50' }}>
            {stats.totalTrades}
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>
            Total
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#28a745' }}>
            {stats.completedTrades}
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>
            Completed
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#007bff' }}>
            {stats.successRate}%
          </div>
          <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>
            Success
          </div>
        </div>
      </div>

      {/* Recent Trades */}
      <div style={{ marginBottom: '12px' }}>
        <h4 style={{ 
          margin: '0 0 12px 0', 
          fontSize: '14px', 
          color: '#495057',
          fontWeight: '600'
        }}>
          Recent Trades
        </h4>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {trades.slice(0, 3).map((trade) => (
            <div key={trade.id} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              background: '#fafbfc',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '10px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  background: trade.trade_type === 'buy' ? '#d4edda' : '#f8d7da',
                  color: trade.trade_type === 'buy' ? '#155724' : '#721c24'
                }}>
                  {trade.trade_type}
                </span>
                <span style={{ color: '#495057' }}>
                  {getTokenSymbol(trade.input_token)} → {getTokenSymbol(trade.output_token)}
                </span>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#6c757d' }}>
                  {formatDate(trade.created_at)}
                </span>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: getStatusColor(trade.status)
                }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* View All Link */}
      <div style={{ textAlign: 'center', marginTop: '12px' }}>
        <span
          onClick={onViewAll}
          style={{
            color: '#007bff',
            fontSize: '12px',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontWeight: '500'
          }}
        >
          View All Trading History →
        </span>
      </div>
    </div>
  );
};

export default TradingHistorySummary;